import React, { useState, useEffect, useCallback, useRef } from 'react';
import songsData from './songs.json';
import { supabase } from './supabase';
import { 
  Home, Search, Shuffle, Compass, Mic2, Info, Heart, ListMusic, 
  Image, Volume2, Moon, Sun, Play, Pause, SkipBack, SkipForward, 
  Repeat, X, Plus, ChevronRight, Music, Download, Upload, Trash2,
  Clock, TrendingUp, Sparkles, ExternalLink, Copy, Check, Globe, Lock,
  ChevronLeft, MoreHorizontal, VolumeX, List, GripVertical, ArrowUpDown,
  History, ChevronUp, ChevronDown, Loader2, Link, Tag, Menu, Bug, Mail, Construction, Award, Twitter
} from 'lucide-react';


// Available moods
const MOODS = ['hype', 'happy', 'comfy', 'chill', 'sad', 'melancholy', 'epic', 'romantic'];

const SAMPLE_SOUNDBITES = [];

const SAMPLE_FANART = [];

// ============================================
// UTILITY FUNCTIONS
// ============================================
const formatTime = (seconds) => {
  if (isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const getMatchingArt = (song, fanArt) => {
  const matching = fanArt.filter(art => 
    art.tags.some(tag => song.tags.includes(tag))
  );
  return matching.length > 0 ? matching[Math.floor(Math.random() * matching.length)] : null;
};

// ============================================
// MAIN APP COMPONENT
// ============================================
export default function PippaKaraokeArchive() {
  // Theme
  const [darkMode, setDarkMode] = useState(true);
  
  // Navigation
  const [currentView, setCurrentView] = useState('home');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Data
  const [songs] = useState(songsData);
  const [soundBites, setSoundBites] = useState(SAMPLE_SOUNDBITES);
  const [fanArt, setFanArt] = useState(SAMPLE_FANART);
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [communityPlaylists, setCommunityPlaylists] = useState([]);
  const [favorites, setFavorites] = useState([]);
  
  // Player state
  const [currentSong, setCurrentSong] = useState(null);
  const [currentList, setCurrentList] = useState([]); // The list we're playing from
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [queue, setQueue] = useState([]);
  const [showQueue, setShowQueue] = useState(false);
  
  // Audio ref
  const audioRef = useRef(new Audio());
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTag, setSearchTag] = useState('all');
  const [searchMood, setSearchMood] = useState('all');
  const [sortBy, setSortBy] = useState('date'); // date, popularity, title, artist
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  
  // Random mode state
  const [randomTags, setRandomTags] = useState([]); // Empty = all tags enabled
  
  // Recently played
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  
  // Song details modal
  const [showSongDetails, setShowSongDetails] = useState(false);
  const [selectedSongDetails, setSelectedSongDetails] = useState(null);
  
  // Modals
  const [showAddToPlaylist, setShowAddToPlaylist] = useState(false);
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [showUploadArt, setShowUploadArt] = useState(false);
  const [showUploadSoundBite, setShowUploadSoundBite] = useState(false);
  const [showNicknamePrompt, setShowNicknamePrompt] = useState(false);
  const [selectedSongForPlaylist, setSelectedSongForPlaylist] = useState(null);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [pendingPublishId, setPendingPublishId] = useState(null);
  
  // User identity
  const [userNickname, setUserNickname] = useState('');
  const [nicknameInput, setNicknameInput] = useState('');
  
  // Admin
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Form state
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newArtTags, setNewArtTags] = useState('');
  const [newArtCredit, setNewArtCredit] = useState('');
  const [newSoundBiteName, setNewSoundBiteName] = useState('');
  const [newSoundBiteCredit, setNewSoundBiteCredit] = useState('');
  
  // Loading
  const [isLoading, setIsLoading] = useState(true);
  
  // Custom loading gif URL (set your own)
  const loadingGif = null; // Replace with: "https://your-url.com/loading.gif"

  // Get unique data
  const allTags = [...new Set(songs.flatMap(s => s.tags))].sort();
  const allArtists = [...new Set(songs.map(s => s.artist))].sort();

  // ============================================
  // STORAGE - Load and Save
  // ============================================
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load local storage data
        const playlists = localStorage.getItem('pippa-user-playlists');
        const favs = localStorage.getItem('pippa-favorites');
        const art = localStorage.getItem('pippa-fanart');
        const bites = localStorage.getItem('pippa-soundbites');
        const theme = localStorage.getItem('pippa-theme');
        const recent = localStorage.getItem('pippa-recently-played');
        const nickname = localStorage.getItem('pippa-nickname');
        
        if (playlists) setUserPlaylists(JSON.parse(playlists));
        if (favs) setFavorites(JSON.parse(favs));
        if (art) setFanArt(JSON.parse(art));
        if (bites) setSoundBites(JSON.parse(bites));
        if (theme) setDarkMode(JSON.parse(theme));
        if (recent) setRecentlyPlayed(JSON.parse(recent));
        if (nickname) setUserNickname(nickname);
        
        // Load community playlists from Supabase
        const { data, error } = await supabase
          .from('public_playlists')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) {
          console.log('Error loading community playlists:', error);
        } else if (data) {
          setCommunityPlaylists(data.map(p => ({
            id: p.id,
            name: p.name,
            songs: p.songs,
            createdBy: p.created_by,
            displayName: p.display_name || 'Anonymous',
            isPublic: true
          })));
        }
      } catch (e) {
        console.log('Error loading data:', e);
      }
      setIsLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('pippa-user-playlists', JSON.stringify(userPlaylists));
    }
  }, [userPlaylists, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('pippa-favorites', JSON.stringify(favorites));
    }
  }, [favorites, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('pippa-fanart', JSON.stringify(fanArt));
    }
  }, [fanArt, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('pippa-soundbites', JSON.stringify(soundBites));
    }
  }, [soundBites, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('pippa-theme', JSON.stringify(darkMode));
    }
  }, [darkMode, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('pippa-recently-played', JSON.stringify(recentlyPlayed));
    }
  }, [recentlyPlayed, isLoading]);

  // ============================================
  // PLAYER FUNCTIONS
  // ============================================
  const playSong = useCallback((song, listToPlay = null) => {
    const audio = audioRef.current;
    
    if (song.audioUrl) {
      audio.src = song.audioUrl;
      audio.play().catch(e => console.log('Playback error:', e));
    }
    
    setCurrentSong(song);
    setIsPlaying(true);
    setProgress(0);
    
    // Track recently played (keep last 20, no duplicates)
    setRecentlyPlayed(prev => {
      const filtered = prev.filter(id => id !== song.id);
      return [song.id, ...filtered].slice(0, 20);
    });
    
    // If a list is provided, set it as our current playlist
    if (listToPlay && listToPlay.length > 0) {
      setCurrentList(listToPlay);
    }
  }, []);

  const playRandom = useCallback((tagFilter = []) => {
    // Filter songs by tags if any are selected
    const availableSongs = tagFilter.length > 0 
      ? songs.filter(song => song.tags.some(tag => tagFilter.includes(tag)))
      : songs;
    
    if (availableSongs.length === 0) return;
    
    const randomSong = availableSongs[Math.floor(Math.random() * availableSongs.length)];
    setShuffle(true); // Enable shuffle so it keeps picking random
    playSong(randomSong, availableSongs);
  }, [songs, playSong]);

  const handleNext = useCallback(() => {
    // First check manual queue
    if (queue.length > 0) {
      const nextSong = shuffle 
        ? queue[Math.floor(Math.random() * queue.length)]
        : queue[0];
      setQueue(queue.filter(s => s.id !== nextSong.id));
      playSong(nextSong);
      return;
    }
    
    // Otherwise use current list
    const list = currentList.length > 0 ? currentList : songs;
    if (list.length === 0) return;
    
    const currentIndex = list.findIndex(s => s.id === currentSong?.id);
    
    if (shuffle) {
      const randomIndex = Math.floor(Math.random() * list.length);
      playSong(list[randomIndex]);
    } else if (currentIndex < list.length - 1) {
      playSong(list[currentIndex + 1]);
    } else if (repeat) {
      playSong(list[0]); // Loop back to start
    }
    // If not repeat and at end, just stop
  }, [queue, shuffle, repeat, currentSong, currentList, songs, playSong]);

  const handlePrev = useCallback(() => {
    const audio = audioRef.current;
    
    // If more than 3 seconds in, restart current song
    if (audio.currentTime > 3) {
      audio.currentTime = 0;
      return;
    }
    
    // Otherwise go to previous song
    const list = currentList.length > 0 ? currentList : songs;
    if (list.length === 0) return;
    
    const currentIndex = list.findIndex(s => s.id === currentSong?.id);
    
    if (currentIndex > 0) {
      playSong(list[currentIndex - 1]);
    } else if (repeat) {
      playSong(list[list.length - 1]); // Loop to end
    } else {
      audio.currentTime = 0; // Just restart current song
    }
  }, [currentSong, currentList, songs, repeat, playSong]);

  const addToQueue = (song) => {
    if (!queue.find(s => s.id === song.id)) {
      setQueue([...queue, song]);
    }
  };

  const removeFromQueue = (songId) => {
    setQueue(queue.filter(s => s.id !== songId));
  };

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    let lastUpdate = 0;
    
    const handleTimeUpdate = () => {
      if (audio.duration) {
        const now = Date.now();
        // Only update once per second to prevent constant re-renders
        if (now - lastUpdate > 1000) {
          setProgress((audio.currentTime / audio.duration) * 100);
          lastUpdate = now;
        }
      }
    };
    
    const handleEnded = () => {
      handleNext();
    };
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [handleNext]);

  // Handle play/pause
  useEffect(() => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.play().catch(e => console.log('Playback error:', e));
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  // Handle volume
  useEffect(() => {
    audioRef.current.volume = isMuted ? 0 : volume;
  }, [volume, isMuted]);

  // Seek function
  const handleSeek = (e) => {
    const audio = audioRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audio.currentTime = percent * audio.duration;
  };

  // ============================================
  // KEYBOARD SHORTCUTS
  // ============================================
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch(e.code) {
        case 'Space':
          e.preventDefault();
          if (currentSong) setIsPlaying(prev => !prev);
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePrev();
          break;
        case 'ArrowUp':
          e.preventDefault();
          setVolume(prev => Math.min(1, prev + 0.1));
          setIsMuted(false);
          break;
        case 'ArrowDown':
          e.preventDefault();
          setVolume(prev => Math.max(0, prev - 0.1));
          break;
        case 'KeyM':
          setIsMuted(prev => !prev);
          break;
        case 'KeyS':
          setShuffle(prev => !prev);
          break;
        case 'KeyR':
          setRepeat(prev => !prev);
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSong, handleNext, handlePrev]);

  // ============================================
  // DATA FUNCTIONS
  // ============================================
  
  // Download a single song
  const downloadSong = (song) => {
    const link = document.createElement('a');
    link.href = song.audioUrl;
    link.download = `${song.title} - ${song.artist}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download playlist as M3U file
  const downloadPlaylistM3U = (playlist) => {
    const playlistSongs = playlist.songs.map(id => songs.find(s => s.id === id)).filter(Boolean);
    
    let m3uContent = '#EXTM3U\n';
    playlistSongs.forEach(song => {
      m3uContent += `#EXTINF:${song.durationSec},${song.title} - ${song.artist}\n`;
      m3uContent += `${song.audioUrl}\n`;
    });
    
    const blob = new Blob([m3uContent], { type: 'audio/x-mpegurl' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${playlist.name}.m3u`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download all songs in playlist as ZIP
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [zipProgress, setZipProgress] = useState({ current: 0, total: 0 });

  const downloadPlaylistZip = async (playlist) => {
    const playlistSongs = playlist.songs.map(id => songs.find(s => s.id === id)).filter(Boolean);
    
    if (playlistSongs.length === 0) return;
    
    // Confirm before downloading many files
    if (playlistSongs.length > 5) {
      const confirmed = window.confirm(`This will download ${playlistSongs.length} files. Your browser may ask for permission to download multiple files. Continue?`);
      if (!confirmed) return;
    }
    
    setIsDownloadingZip(true);
    setZipProgress({ current: 0, total: playlistSongs.length });
    
    try {
      // Download each song individually with a delay
      for (let i = 0; i < playlistSongs.length; i++) {
        const song = playlistSongs[i];
        setZipProgress({ current: i + 1, total: playlistSongs.length });
        
        // Trigger download using anchor element (bypasses CORS)
        const link = document.createElement('a');
        link.href = song.audioUrl;
        link.download = `${String(i + 1).padStart(2, '0')} - ${song.title} - ${song.artist}.mp3`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Small delay between downloads to avoid overwhelming browser
        if (i < playlistSongs.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    } catch (err) {
      console.error('Failed to download:', err);
      alert('Failed to download playlist. Please try again.');
    } finally {
      setIsDownloadingZip(false);
      setZipProgress({ current: 0, total: 0 });
    }
  };

  const toggleFavorite = (songId) => {
    setFavorites(prev => 
      prev.includes(songId) 
        ? prev.filter(id => id !== songId)
        : [...prev, songId]
    );
  };

  // Get or create device ID for ownership tracking
  const getDeviceId = () => {
    let deviceId = localStorage.getItem('pippa-device-id');
    if (!deviceId) {
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('pippa-device-id', deviceId);
    }
    return deviceId;
  };

  const createPlaylist = () => {
    if (newPlaylistName.trim()) {
      const newPlaylist = {
        id: Date.now(),
        name: newPlaylistName.trim(),
        songs: [],
        isPublic: false,
        createdBy: getDeviceId(),
        createdAt: new Date().toISOString()
      };
      
      setUserPlaylists([...userPlaylists, newPlaylist]);
      setNewPlaylistName('');
      setShowCreatePlaylist(false);
    }
  };

  // Publish playlist to community (with all current songs)
  // Prompt for nickname before publishing
  const initiatePublish = (playlistId) => {
    if (!userNickname) {
      setPendingPublishId(playlistId);
      setShowNicknamePrompt(true);
    } else {
      publishPlaylist(playlistId);
    }
  };

  const saveNickname = () => {
    if (nicknameInput.trim()) {
      const nickname = nicknameInput.trim();
      setUserNickname(nickname);
      localStorage.setItem('pippa-nickname', nickname);
      setShowNicknamePrompt(false);
      setNicknameInput('');
      
      // If there's a pending publish, do it now
      if (pendingPublishId) {
        publishPlaylist(pendingPublishId, nickname);
        setPendingPublishId(null);
      }
    }
  };

  const publishPlaylist = async (playlistId, overrideNickname = null) => {
    const playlist = userPlaylists.find(p => p.id === playlistId);
    if (!playlist || playlist.isPublic) return;
    
    const deviceId = getDeviceId();
    const displayName = overrideNickname || userNickname || 'Anonymous';
    
    const { data, error } = await supabase
      .from('public_playlists')
      .insert({
        name: playlist.name,
        songs: playlist.songs,
        created_by: deviceId,
        display_name: displayName
      })
      .select()
      .single();
    
    if (error) {
      console.log('Error publishing playlist:', error);
      return;
    }
    
    if (data) {
      // Update local playlist with Supabase ID and mark as public
      setUserPlaylists(prev => prev.map(p => 
        p.id === playlistId 
          ? { ...p, id: data.id, isPublic: true, createdBy: deviceId }
          : p
      ));
      
      // Update selectedPlaylist if it's the one being published
      if (selectedPlaylist?.id === playlistId) {
        setSelectedPlaylist(prev => ({ ...prev, id: data.id, isPublic: true, createdBy: deviceId }));
      }
      
      setCommunityPlaylists([...communityPlaylists, {
        id: data.id,
        name: data.name,
        songs: data.songs,
        createdBy: deviceId,
        displayName: displayName,
        isPublic: true
      }]);
    }
  };

  const addSongToPlaylist = async (playlistId) => {
    if (selectedSongForPlaylist) {
      const playlist = userPlaylists.find(p => p.id === playlistId);
      
      setUserPlaylists(prev => prev.map(p => {
        if (p.id === playlistId && !p.songs.includes(selectedSongForPlaylist.id)) {
          const updated = { ...p, songs: [...p.songs, selectedSongForPlaylist.id] };
          return updated;
        }
        return p;
      }));
      
      // Update Supabase if public
      if (playlist?.isPublic) {
        const newSongs = [...playlist.songs, selectedSongForPlaylist.id];
        const { error } = await supabase
          .from('public_playlists')
          .update({ songs: newSongs })
          .eq('id', playlistId);
        
        if (error) {
          console.log('Error updating playlist:', error);
        } else {
          setCommunityPlaylists(cp => cp.map(c => 
            c.id === playlistId ? { ...c, songs: newSongs } : c
          ));
        }
      }
      
      setSelectedSongForPlaylist(null);
      setShowAddToPlaylist(false);
    }
  };

  const removeSongFromPlaylist = async (playlistId, songId) => {
    const playlist = userPlaylists.find(p => p.id === playlistId);
    const newSongs = playlist.songs.filter(id => id !== songId);
    
    setUserPlaylists(prev => prev.map(p => {
      if (p.id === playlistId) {
        return { ...p, songs: newSongs };
      }
      return p;
    }));
    
    // Update Supabase if public
    if (playlist?.isPublic) {
      const { error } = await supabase
        .from('public_playlists')
        .update({ songs: newSongs })
        .eq('id', playlistId);
      
      if (!error) {
        setCommunityPlaylists(cp => cp.map(c => 
          c.id === playlistId ? { ...c, songs: newSongs } : c
        ));
      }
    }
  };

  const deletePlaylist = async (playlistId) => {
    const playlist = userPlaylists.find(p => p.id === playlistId);
    const deviceId = getDeviceId();
    
    // Only allow deletion if you created it
    if (playlist?.isPublic && playlist?.createdBy !== deviceId) {
      console.log('Cannot delete: not your playlist');
      return;
    }
    
    setUserPlaylists(prev => prev.filter(p => p.id !== playlistId));
    setCommunityPlaylists(prev => prev.filter(p => p.id !== playlistId));
    
    // Delete from Supabase if public
    if (playlist?.isPublic) {
      await supabase
        .from('public_playlists')
        .delete()
        .eq('id', playlistId);
    }
    
    if (selectedPlaylist?.id === playlistId) {
      setSelectedPlaylist(null);
      setCurrentView('home');
    }
  };

  // Unpublish - remove from community but keep locally
  const unpublishPlaylist = async (playlistId) => {
    const playlist = userPlaylists.find(p => p.id === playlistId);
    const deviceId = getDeviceId();
    
    if (!playlist?.isPublic || playlist?.createdBy !== deviceId) return;
    
    await supabase
      .from('public_playlists')
      .delete()
      .eq('id', playlistId);
    
    setUserPlaylists(prev => prev.map(p => 
      p.id === playlistId ? { ...p, isPublic: false } : p
    ));
    setCommunityPlaylists(prev => prev.filter(p => p.id !== playlistId));
    
    if (selectedPlaylist?.id === playlistId) {
      setSelectedPlaylist(prev => ({ ...prev, isPublic: false }));
    }
  };

  // Admin functions - checks Supabase for admin status
  const checkAdminStatus = async () => {
    const deviceId = getDeviceId();
    const { data, error } = await supabase
      .from('admins')
      .select('*')
      .eq('device_id', deviceId)
      .single();
    
    if (!error && data) {
      setIsAdmin(true);
    }
  };

  // Check admin status on load
  useEffect(() => {
    checkAdminStatus();
  }, []);

  const adminDeletePlaylist = async (playlistId) => {
    if (!isAdmin) return;
    
    await supabase
      .from('public_playlists')
      .delete()
      .eq('id', playlistId);
    
    setCommunityPlaylists(prev => prev.filter(p => p.id !== playlistId));
  };

  const uploadFanArt = () => {
    if (newArtTags.trim()) {
      const newArt = {
        id: Date.now(),
        artist: newArtCredit.trim() || 'Anonymous',
        tags: newArtTags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean),
        imageUrl: null, // Placeholder - would be actual upload URL
        dateAdded: new Date().toISOString()
      };
      setFanArt([newArt, ...fanArt]);
      setNewArtTags('');
      setNewArtCredit('');
      setShowUploadArt(false);
    }
  };

  const deleteFanArt = (artId) => {
    setFanArt(prev => prev.filter(a => a.id !== artId));
  };

  const uploadSoundBite = () => {
    if (newSoundBiteName.trim()) {
      const newBite = {
        id: Date.now(),
        name: newSoundBiteName.trim(),
        duration: "0:03", // Placeholder
        likes: 0,
        uploadedBy: newSoundBiteCredit.trim() || 'Anonymous',
        dateAdded: new Date().toISOString()
      };
      setSoundBites([newBite, ...soundBites]);
      setNewSoundBiteName('');
      setNewSoundBiteCredit('');
      setShowUploadSoundBite(false);
    }
  };

  const likeSoundBite = (biteId) => {
    setSoundBites(prev => prev.map(b => 
      b.id === biteId ? { ...b, likes: b.likes + 1 } : b
    ));
  };

  const deleteSoundBite = (biteId) => {
    setSoundBites(prev => prev.filter(b => b.id !== biteId));
  };

  // ============================================
  // FILTERED DATA
  // ============================================
  const filteredSongs = songs
    .filter(s => {
      const matchesSearch = !searchQuery || 
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.artist.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.stream.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTag = searchTag === 'all' || s.tags.includes(searchTag);
      const matchesMood = searchMood === 'all' || s.mood === searchMood;
      return matchesSearch && matchesTag && matchesMood;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch(sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'artist':
          comparison = a.artist.localeCompare(b.artist);
          break;
        case 'popularity':
          comparison = b.plays - a.plays;
          break;
        case 'date':
        default:
          comparison = new Date(b.date) - new Date(a.date);
          break;
      }
      return sortOrder === 'asc' ? -comparison : comparison;
    });

  const artistSongs = selectedArtist 
    ? songs.filter(s => s.artist === selectedArtist)
    : [];

  // Get song recommendations based on tags/artist
  const getRecommendations = (song, limit = 5) => {
    if (!song) return [];
    
    return songs
      .filter(s => s.id !== song.id)
      .map(s => {
        // Score based on matching tags and artist
        let score = 0;
        const matchingTags = s.tags.filter(tag => song.tags.includes(tag));
        score += matchingTags.length * 2;
        if (s.artist === song.artist) score += 3;
        return { ...s, score };
      })
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);
  };

  // ============================================
  // THEME CLASSES
  // ============================================
  const theme = {
    bg: darkMode ? 'bg-zinc-950' : 'bg-pink-50',
    bgSecondary: darkMode ? 'bg-zinc-900' : 'bg-white',
    bgTertiary: darkMode ? 'bg-zinc-800' : 'bg-pink-100/50',
    bgHover: darkMode ? 'hover:bg-zinc-800' : 'hover:bg-pink-100',
    bgActive: darkMode ? 'bg-purple-500/20' : 'bg-pink-100',
    text: darkMode ? 'text-gray-100' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-400' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-500' : 'text-gray-400',
    border: darkMode ? 'border-zinc-700' : 'border-pink-200',
    input: darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-pink-200',
    accent: darkMode ? 'purple' : 'pink',
  };

  // ============================================
  // COMPONENTS
  // ============================================
  const SongRow = ({ song, songList = [], showArtist = true, inPlaylist = false, playlistId = null }) => {
    const isFavorite = favorites.includes(song.id);
    const isCurrentSong = currentSong?.id === song.id;
    const matchedArt = getMatchingArt(song, fanArt);
    
    return (
      <div 
        className={`group flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
          isCurrentSong 
            ? `${darkMode ? 'bg-purple-500/20 border-purple-400/30' : 'bg-pink-500/20 border-pink-400/30'} border` 
            : `${theme.bgHover} border border-transparent`
        }`}
        onClick={() => playSong(song, songList.length > 0 ? songList : songs)}
      >
        {/* Thumbnail */}
        <div className={`relative w-12 h-12 rounded-lg bg-gradient-to-br ${darkMode ? 'from-purple-400/50 to-indigo-500/50' : 'from-pink-400/50 to-rose-500/50'} flex items-center justify-center flex-shrink-0 overflow-hidden`}>
          {matchedArt?.imageUrl ? (
            <img src={matchedArt.imageUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <Music size={20} className={darkMode ? 'text-white/60' : 'text-gray-400'} />
          )}
          {isCurrentSong && isPlaying && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <div className="flex gap-0.5">
                <span className={`w-1 h-3 ${darkMode ? 'bg-purple-400' : 'bg-pink-400'} rounded-full animate-pulse`}></span>
                <span className={`w-1 h-4 ${darkMode ? 'bg-purple-400' : 'bg-pink-400'} rounded-full animate-pulse`} style={{animationDelay: '150ms'}}></span>
                <span className={`w-1 h-2 ${darkMode ? 'bg-purple-400' : 'bg-pink-400'} rounded-full animate-pulse`} style={{animationDelay: '300ms'}}></span>
              </div>
            </div>
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className={`font-medium truncate ${theme.text}`}>{song.title}</p>
          <p className={`text-sm truncate ${theme.textSecondary}`}>
            {showArtist && `${song.artist} • `}{song.stream}
          </p>
        </div>
        
        {/* Mood & Tags */}
        <div className="hidden md:flex items-center gap-1">
          {song.mood && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              darkMode ? 'bg-indigo-500/30 text-indigo-300' : 'bg-indigo-100 text-indigo-600'
            }`}>
              {song.mood}
            </span>
          )}
          {song.tags.slice(0, 2).map(tag => (
            <span key={tag} className={`px-2 py-0.5 text-xs rounded-full ${darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-pink-100 text-pink-600'}`}>
              {tag}
            </span>
          ))}
        </div>
        
        {/* Duration */}
        <span className={`text-sm ${theme.textMuted} w-12 text-right`}>{song.duration}</span>
        
        {/* Actions */}
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            onClick={(e) => { e.stopPropagation(); toggleFavorite(song.id); }}
            className={`p-2 rounded-full transition-colors ${isFavorite ? 'text-pink-500' : `${theme.textMuted} hover:text-pink-400`}`}
          >
            <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); addToQueue(song); }}
            className={`p-2 rounded-full ${theme.textMuted} hover:text-white transition-colors`}
            title="Add to queue"
          >
            <List size={16} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); downloadSong(song); }}
            className={`p-2 rounded-full ${theme.textMuted} hover:text-green-400 transition-colors`}
            title="Download"
          >
            <Download size={16} />
          </button>
          {inPlaylist ? (
            <button 
              onClick={(e) => { e.stopPropagation(); removeSongFromPlaylist(playlistId, song.id); }}
              className="p-2 rounded-full text-red-400 hover:text-red-300 transition-colors"
            >
              <Trash2 size={16} />
            </button>
          ) : (
            <button 
              onClick={(e) => { e.stopPropagation(); setSelectedSongForPlaylist(song); setShowAddToPlaylist(true); }}
              className={`p-2 rounded-full ${theme.textMuted} hover:text-pink-400 transition-colors`}
            >
              <Plus size={16} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const PlaylistCard = ({ playlist, onClick }) => (
    <div 
      onClick={onClick}
      className={`group p-4 rounded-xl ${theme.bgTertiary} border ${theme.border} ${darkMode ? 'hover:border-purple-500/30' : 'hover:border-pink-500/30'} cursor-pointer transition-all hover:scale-[1.02]`}
    >
      <div className={`w-full aspect-square rounded-lg bg-gradient-to-br ${darkMode ? 'from-purple-500/30 to-indigo-600/30' : 'from-pink-500/30 to-rose-600/30'} mb-3 flex items-center justify-center`}>
        <ListMusic size={32} className={darkMode ? 'text-purple-400/60' : 'text-pink-400/60'} />
      </div>
      <h3 className={`font-semibold truncate ${theme.text}`}>{playlist.name}</h3>
      <p className={`text-sm ${theme.textMuted}`}>
        {playlist.songs.length} songs • by {playlist.displayName || 'Anonymous'}
      </p>
      <div className="flex items-center gap-1 mt-1">
        {playlist.isPublic ? (
          <Globe size={12} className="text-green-400" />
        ) : (
          <Lock size={12} className={theme.textMuted} />
        )}
        <span className={`text-xs ${theme.textMuted}`}>
          {playlist.isPublic ? 'Public' : 'Private'}
        </span>
      </div>
    </div>
  );

  const ArtistCard = ({ artist, songCount, onClick }) => (
    <div 
      onClick={onClick}
      className={`group p-4 rounded-xl ${theme.bgTertiary} border ${theme.border} ${darkMode ? 'hover:border-purple-500/30' : 'hover:border-pink-500/30'} cursor-pointer transition-all hover:scale-[1.02]`}
    >
      <div className={`w-full aspect-square rounded-full bg-gradient-to-br ${darkMode ? 'from-purple-500/30 to-indigo-600/30' : 'from-pink-500/30 to-rose-600/30'} mb-3 flex items-center justify-center`}>
        <Mic2 size={32} className={darkMode ? 'text-purple-400/60' : 'text-pink-400/60'} />
      </div>
      <h3 className={`font-semibold truncate text-center ${theme.text}`}>{artist}</h3>
      <p className={`text-sm text-center ${theme.textMuted}`}>{songCount} songs</p>
    </div>
  );

  // ============================================
  // VIEW RENDERERS
  // ============================================
  const renderHome = () => (
    <div className="space-y-8">
      {/* Hero */}
      <div className={`relative rounded-2xl overflow-hidden bg-gradient-to-br ${darkMode ? 'from-purple-900/30 via-purple-800/20 to-indigo-900/30' : 'from-pink-200/50 via-pink-100/50 to-rose-200/50'} p-8`}>
        <div className="relative z-10">
          <h1 className={`text-4xl font-bold mb-2 ${theme.text}`}>Welcome to the Archive</h1>
          <p className={theme.textSecondary}>
            Your home for all Pipkin Pippa karaoke performances. Browse, listen, and create playlists!
          </p>
          <div className="flex gap-3 mt-4">
            <button 
              onClick={playRandom}
              className={`flex items-center gap-2 px-5 py-2.5 ${darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-pink-500 hover:bg-pink-400'} text-white rounded-full font-medium transition-colors`}
            >
              <Shuffle size={18} />
              Play Random
            </button>
            <button 
              onClick={() => setCurrentView('search')}
              className={`flex items-center gap-2 px-5 py-2.5 ${theme.bgTertiary} ${theme.text} rounded-full font-medium transition-colors ${theme.bgHover}`}
            >
              <Search size={18} />
              Browse All
            </button>
          </div>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Music, label: 'Total Songs', value: songs.length },
          { icon: Mic2, label: 'Artists Covered', value: allArtists.length },
          { icon: TrendingUp, label: 'Total Plays', value: songs.reduce((a, s) => a + s.plays, 0).toLocaleString() },
          { icon: Heart, label: 'Your Favorites', value: favorites.length },
        ].map((stat, i) => (
          <div key={i} className={`p-4 rounded-xl ${theme.bgTertiary} border ${theme.border}`}>
            <stat.icon size={20} className={`mb-2 ${darkMode ? 'text-purple-400' : 'text-pink-400'}`} />
            <p className={`text-2xl font-bold ${theme.text}`}>{stat.value}</p>
            <p className={`text-sm ${theme.textMuted}`}>{stat.label}</p>
          </div>
        ))}
      </div>
      
      {/* Popular Songs */}
      <div>
        <h2 className={`text-xl font-semibold mb-4 ${theme.text}`}>🔥 Most Popular</h2>
        <div className="space-y-1">
          {(() => {
            const popularSongs = [...songs].sort((a, b) => b.plays - a.plays).slice(0, 5);
            return popularSongs.map(song => (
              <SongRow key={song.id} song={song} songList={popularSongs} />
            ));
          })()}
        </div>
      </div>
      
      {/* Recent Additions */}
      <div>
        <h2 className={`text-xl font-semibold mb-4 ${theme.text}`}>✨ Recently Added</h2>
        <div className="space-y-1">
          {(() => {
            const recentSongs = [...songs].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);
            return recentSongs.map(song => (
              <SongRow key={song.id} song={song} songList={recentSongs} />
            ));
          })()}
        </div>
      </div>
    </div>
  );

  const renderSearch = () => (
    <div className="space-y-6">
      <h1 className={`text-2xl font-bold ${theme.text}`}>Search Archive</h1>
      
      {/* Search Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 ${theme.textMuted}`} size={18} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search songs, artists, streams..."
            className={`w-full pl-12 pr-4 py-3 ${theme.input} border rounded-xl focus:outline-none focus:border-pink-500/50 ${theme.text}`}
          />
        </div>
        <select
          value={searchTag}
          onChange={(e) => setSearchTag(e.target.value)}
          className={`px-4 py-3 ${theme.input} border rounded-xl focus:outline-none focus:border-pink-500/50 ${theme.text}`}
        >
          <option value="all">All Tags</option>
          {allTags.map(tag => (
            <option key={tag} value={tag}>{tag}</option>
          ))}
        </select>
        <select
          value={searchMood}
          onChange={(e) => setSearchMood(e.target.value)}
          className={`px-4 py-3 ${theme.input} border rounded-xl focus:outline-none focus:border-pink-500/50 ${theme.text}`}
        >
          <option value="all">All Moods</option>
          {MOODS.map(mood => (
            <option key={mood} value={mood}>{mood.charAt(0).toUpperCase() + mood.slice(1)}</option>
          ))}
        </select>
      </div>
      
      {/* Sort Controls */}
      <div className="flex items-center gap-4">
        <span className={`text-sm ${theme.textMuted}`}>Sort by:</span>
        <div className="flex gap-2">
          {[
            { value: 'date', label: 'Date' },
            { value: 'popularity', label: 'Popular' },
            { value: 'title', label: 'Title' },
            { value: 'artist', label: 'Artist' },
          ].map(option => (
            <button
              key={option.value}
              onClick={() => {
                if (sortBy === option.value) {
                  setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
                } else {
                  setSortBy(option.value);
                  setSortOrder('desc');
                }
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                sortBy === option.value
                  ? darkMode 
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                    : 'bg-pink-100 text-pink-600 border border-pink-300'
                  : `${theme.bgTertiary} ${theme.textSecondary} border ${theme.border}`
              }`}
            >
              {option.label}
              {sortBy === option.value && (
                sortOrder === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* Results */}
      <div className="space-y-1">
        <p className={`text-sm ${theme.textMuted} mb-2`}>{filteredSongs.length} songs found</p>
        {filteredSongs.map(song => (
          <SongRow key={song.id} song={song} songList={filteredSongs} />
        ))}
        {filteredSongs.length === 0 && (
          <div className={`text-center py-12 ${theme.textMuted}`}>
            <Search size={48} className="mx-auto mb-4 opacity-30" />
            <p>No songs found</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderRandom = () => {
    const toggleTag = (tag) => {
      setRandomTags(prev => 
        prev.includes(tag) 
          ? prev.filter(t => t !== tag)
          : [...prev, tag]
      );
    };

    const filteredByTags = randomTags.length > 0
      ? songs.filter(song => song.tags.some(tag => randomTags.includes(tag)))
      : songs;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className={`text-2xl font-bold ${theme.text}`}>Random Mode</h1>
          <button 
            onClick={() => playRandom(randomTags)}
            className={`flex items-center gap-2 px-5 py-2.5 ${darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-pink-500 hover:bg-pink-400'} text-white rounded-full font-medium transition-colors`}
          >
            <Shuffle size={18} />
            Play Random
          </button>
        </div>

        <p className={theme.textSecondary}>
          Select tags to filter what songs can be randomly picked. Leave all unchecked to include everything.
        </p>

        {/* Tag Toggles */}
        <div className={`p-4 rounded-xl ${theme.bgTertiary} border ${theme.border}`}>
          <h3 className={`font-medium mb-3 ${theme.text}`}>Filter by Tags</h3>
          <div className="flex flex-wrap gap-2">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  randomTags.includes(tag)
                    ? darkMode 
                      ? 'bg-purple-500 text-white' 
                      : 'bg-pink-500 text-white'
                    : `${theme.bgSecondary} ${theme.textSecondary} border ${theme.border}`
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
          
          {randomTags.length > 0 && (
            <button 
              onClick={() => setRandomTags([])}
              className={`mt-3 text-sm ${theme.textMuted} hover:${theme.text}`}
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Preview of available songs */}
        <div>
          <h3 className={`font-medium mb-3 ${theme.text}`}>
            {filteredByTags.length} songs available
            {randomTags.length > 0 && ` (filtered by: ${randomTags.join(', ')})`}
          </h3>
          
          <div className="space-y-1">
            {filteredByTags.slice(0, 10).map(song => (
              <SongRow key={song.id} song={song} songList={filteredByTags} />
            ))}
            {filteredByTags.length > 10 && (
              <p className={`text-center py-2 ${theme.textMuted}`}>
                ...and {filteredByTags.length - 10} more
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderExplore = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-bold ${theme.text}`}>Explore Community Playlists</h1>
      </div>
      
      {communityPlaylists.length === 0 ? (
        <div className={`text-center py-16 ${theme.textMuted}`}>
          <Compass size={48} className="mx-auto mb-4 opacity-30" />
          <p>No public playlists yet</p>
          <p className="text-sm mt-1">Create a playlist and make it public to share!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {communityPlaylists.map(playlist => (
            <PlaylistCard 
              key={playlist.id} 
              playlist={playlist}
              onClick={() => { setSelectedPlaylist(playlist); setCurrentView('playlist-detail'); }}
            />
          ))}
        </div>
      )}
    </div>
  );

  const renderArtists = () => (
    <div className="space-y-6">
      {selectedArtist ? (
        <>
          <button 
            onClick={() => setSelectedArtist(null)}
            className={`flex items-center gap-1 ${theme.textSecondary} hover:${theme.text}`}
          >
            <ChevronLeft size={18} />
            Back to Artists
          </button>
          
          <div className="flex items-end gap-6 mb-6">
            <div className={`w-32 h-32 rounded-full bg-gradient-to-br ${darkMode ? 'from-purple-500 to-indigo-600' : 'from-pink-500 to-rose-600'} flex items-center justify-center`}>
              <Mic2 size={48} className="text-white/80" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted} uppercase tracking-wider`}>Artist</p>
              <h1 className={`text-3xl font-bold ${theme.text}`}>{selectedArtist}</h1>
              <p className={theme.textSecondary}>{artistSongs.length} songs covered</p>
            </div>
          </div>
          
          <div className="space-y-1">
            {artistSongs.map(song => (
              <SongRow key={song.id} song={song} songList={artistSongs} showArtist={false} />
            ))}
          </div>
        </>
      ) : (
        <>
          <h1 className={`text-2xl font-bold ${theme.text}`}>Browse by Artist</h1>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {allArtists.map(artist => (
              <ArtistCard 
                key={artist}
                artist={artist}
                songCount={songs.filter(s => s.artist === artist).length}
                onClick={() => setSelectedArtist(artist)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderAbout = () => (
    <div className="max-w-2xl space-y-6">
      <h1 className={`text-2xl font-bold ${theme.text}`}>About This Project</h1>
      
      <div className={`p-6 rounded-xl ${theme.bgTertiary} border ${theme.border}`}>
        <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${darkMode ? 'from-purple-500 to-purple-700' : 'from-pink-400 to-pink-500'} flex items-center justify-center mb-4`}>
          <span className="text-4xl">{darkMode ? '🦇' : '🐰'}</span>
        </div>
        
        <h2 className={`text-xl font-semibold mb-3 ${theme.text}`}>{darkMode ? 'Shippa' : 'Pipkin Pippa'} Karaoke Archive</h2>
        
        <div className={`space-y-4 ${theme.textSecondary}`}>
          <p>
            Welcome to the unofficial archive of Pipkin Pippa's karaoke performances! 
            This site is a fan project dedicated to preserving and making accessible 
            all of Pippa's wonderful singing moments.
          </p>
          <p>
            Here you can browse through the archive, create your own playlists, 
            discover community favorites, and even contribute fan art and sound bites.
          </p>
          <p>
            This project is not affiliated with Phase Connect or Pipkin Pippa. 
            All content belongs to their respective owners.
          </p>
        </div>
        
        <div className={`mt-6 pt-6 border-t ${theme.border}`}>
          <p className={`text-sm ${theme.textMuted}`}>
            Made with 💖 by the community
          </p>
          <p className={`text-sm mt-2 ${theme.textSecondary}`}>
            Check out the <button onClick={() => setCurrentView('credits')} className={`${darkMode ? 'text-purple-400' : 'text-pink-500'} underline`}>Credits</button> page to see everyone who helped make this possible!
          </p>
          <button
            onClick={() => setCurrentView('admin')}
            className={`text-xs ${theme.textMuted} hover:${theme.text} mt-2 opacity-30 hover:opacity-100 transition-opacity`}
          >
            Admin
          </button>
        </div>
      </div>
      
      {/* Your nickname */}
      <div className={`p-4 rounded-xl ${theme.bgTertiary} border ${theme.border}`}>
        <h3 className={`font-medium mb-2 ${theme.text}`}>Your Display Name</h3>
        {userNickname ? (
          <div className="flex items-center justify-between">
            <p className={theme.textSecondary}>{userNickname}</p>
            <button
              onClick={() => {
                setNicknameInput(userNickname);
                setShowNicknamePrompt(true);
              }}
              className={`text-sm ${darkMode ? 'text-purple-400' : 'text-pink-500'}`}
            >
              Change
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowNicknamePrompt(true)}
            className={`text-sm ${darkMode ? 'text-purple-400' : 'text-pink-500'}`}
          >
            Set a nickname for public playlists
          </button>
        )}
      </div>
    </div>
  );

  // Contributors data - easy to expand later
  const contributors = {
    audioArchive: {
      pippa: [
        { name: 'Crosmatron', twitter: 'Crosmatron', contribution: 'Audio archive compilation', image: '/images/contributors/Crosmatron.jpg' },
      ],
      // Future: lia: [], tenma: [], etc.
    },
    website: [
      // Future: { name: 'Developer', twitter: 'handle', contribution: 'Website development' },
    ],
    specialThanks: [
      // Future: { name: 'Person', twitter: 'handle', contribution: 'Reason' },
    ],
  };

  const ContributorCard = ({ name, twitter, contribution, image }) => (
    <div className={`flex items-center gap-4 p-4 rounded-xl ${theme.bgTertiary} border ${theme.border}`}>
      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${darkMode ? 'from-purple-500/30 to-indigo-500/30' : 'from-pink-500/30 to-rose-500/30'} flex items-center justify-center flex-shrink-0 overflow-hidden`}>
        {image ? (
          <img src={image} alt={name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-lg">{name.charAt(0).toUpperCase()}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-medium ${theme.text}`}>{name}</p>
        <p className={`text-sm ${theme.textMuted}`}>{contribution}</p>
        {twitter && (
          <a
            href={`https://x.com/${twitter}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-sm ${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-pink-500 hover:text-pink-400'} transition-colors`}
          >
            Twitter: @{twitter}
          </a>
        )}
      </div>
    </div>
  );

  const renderCredits = () => (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className={`text-2xl font-bold ${theme.text}`}>Credits</h1>
        <p className={`mt-2 ${theme.textSecondary}`}>
          This project wouldn't be possible without the amazing contributions from the community. 
          Thank you to everyone who helped make this archive a reality!
        </p>
      </div>

      {/* Audio Archive Contributors */}
      <div className="space-y-4">
        <h2 className={`text-lg font-semibold ${theme.text} flex items-center gap-2`}>
          <Music size={20} className={darkMode ? 'text-purple-400' : 'text-pink-400'} />
          Audio Archive Contributors
        </h2>
        
        {/* Pippa */}
        {contributors.audioArchive.pippa && contributors.audioArchive.pippa.length > 0 && (
          <div className="space-y-2">
            <h3 className={`text-sm font-medium ${theme.textMuted} uppercase tracking-wider`}>Pippa</h3>
            <div className="space-y-2">
              {contributors.audioArchive.pippa.map((person, idx) => (
                <ContributorCard key={idx} {...person} />
              ))}
            </div>
          </div>
        )}

        {/* Future talents can be added here */}
        {/* {contributors.audioArchive.lia && ... } */}
      </div>

      {/* Website Contributors - show only if there are any */}
      {contributors.website.length > 0 && (
        <div className="space-y-4">
          <h2 className={`text-lg font-semibold ${theme.text} flex items-center gap-2`}>
            <Globe size={20} className={darkMode ? 'text-purple-400' : 'text-pink-400'} />
            Website Contributors
          </h2>
          <div className="space-y-2">
            {contributors.website.map((person, idx) => (
              <ContributorCard key={idx} {...person} />
            ))}
          </div>
        </div>
      )}

      {/* Special Thanks - show only if there are any */}
      {contributors.specialThanks.length > 0 && (
        <div className="space-y-4">
          <h2 className={`text-lg font-semibold ${theme.text} flex items-center gap-2`}>
            <Heart size={20} className={darkMode ? 'text-purple-400' : 'text-pink-400'} />
            Special Thanks
          </h2>
          <div className="space-y-2">
            {contributors.specialThanks.map((person, idx) => (
              <ContributorCard key={idx} {...person} />
            ))}
          </div>
        </div>
      )}

      {/* Want to contribute? */}
      <div className={`p-4 rounded-xl ${darkMode ? 'bg-purple-500/10 border-purple-500/20' : 'bg-pink-500/10 border-pink-500/20'} border`}>
        <p className={`text-sm ${theme.textSecondary}`}>
          <strong>Want to contribute?</strong> If you have karaoke recordings, fan art, or want to help 
          improve the site, reach out via the <button onClick={() => setCurrentView('bugreport')} className={`${darkMode ? 'text-purple-400' : 'text-pink-500'} underline`}>Bug Report</button> page!
        </p>
      </div>
    </div>
  );

  const renderBugReport = () => (
    <div className="max-w-2xl space-y-6">
      <h1 className={`text-2xl font-bold ${theme.text}`}>Report a Bug</h1>
      
      <div className={`p-6 rounded-xl ${theme.bgTertiary} border ${theme.border}`}>
        <Bug size={48} className={`mb-4 ${darkMode ? 'text-purple-400' : 'text-pink-400'}`} />
        
        <h2 className={`text-xl font-semibold mb-3 ${theme.text}`}>Found something broken?</h2>
        
        <div className={`space-y-4 ${theme.textSecondary}`}>
          <p>
            If you've found a bug, have a feature request, or just want to give feedback, 
            please reach out! Your help makes this site better for everyone.
          </p>
        </div>
        
        <div className={`mt-6 pt-6 border-t ${theme.border} space-y-4`}>
          {/* Email */}
          <a 
            href="mailto:pipparadio@gmail.com?subject=Bug Report - Pippa Karaoke"
            className={`flex items-center gap-3 p-4 rounded-xl ${theme.bgSecondary} border ${theme.border} hover:border-pink-500/30 transition-colors`}
          >
            <div className={`w-10 h-10 rounded-full ${darkMode ? 'bg-purple-500/20' : 'bg-pink-500/20'} flex items-center justify-center`}>
              <Mail size={20} className={darkMode ? 'text-purple-400' : 'text-pink-400'} />
            </div>
            <div>
              <p className={`font-medium ${theme.text}`}>Email</p>
              <p className={`text-sm ${theme.textMuted}`}>pipparadio@gmail.com</p>
            </div>
          </a>
          
          {/* Discord */}
          <a 
            href="https://discord.com/users/tuesdayLLC"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 p-4 rounded-xl ${theme.bgSecondary} border ${theme.border} hover:border-pink-500/30 transition-colors`}
          >
            <div className={`w-10 h-10 rounded-full ${darkMode ? 'bg-purple-500/20' : 'bg-pink-500/20'} flex items-center justify-center`}>
              <span className="text-lg">💬</span>
            </div>
            <div>
              <p className={`font-medium ${theme.text}`}>Discord</p>
              <p className={`text-sm ${theme.textMuted}`}>@tuesdayLLC</p>
            </div>
          </a>
        </div>
        
        <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-purple-500/10' : 'bg-pink-500/10'}`}>
          <p className={`text-sm ${theme.textSecondary}`}>
            <strong>When reporting a bug, please include:</strong>
          </p>
          <ul className={`text-sm ${theme.textMuted} mt-2 space-y-1`}>
            <li>• What you were trying to do</li>
            <li>• What happened instead</li>
            <li>• Your device/browser (e.g., iPhone Safari, Chrome on Windows)</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderFavorites = () => (
    <div className="space-y-6">
      <h1 className={`text-2xl font-bold ${theme.text}`}>Your Favorites</h1>
      
      {favorites.length === 0 ? (
        <div className={`text-center py-16 ${theme.textMuted}`}>
          <Heart size={48} className="mx-auto mb-4 opacity-30" />
          <p>No favorites yet</p>
          <p className="text-sm mt-1">Click the heart on any song to add it here!</p>
        </div>
      ) : (
        <div className="space-y-1">
          {(() => {
            const favoriteSongs = favorites.map(id => songs.find(s => s.id === id)).filter(Boolean);
            return favoriteSongs.map(song => (
              <SongRow key={song.id} song={song} songList={favoriteSongs} />
            ));
          })()}
        </div>
      )}
    </div>
  );

  const renderHistory = () => {
    const historySongs = recentlyPlayed.map(id => songs.find(s => s.id === id)).filter(Boolean);
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className={`text-2xl font-bold ${theme.text}`}>Recently Played</h1>
          {recentlyPlayed.length > 0 && (
            <button 
              onClick={() => setRecentlyPlayed([])}
              className={`text-sm ${theme.textMuted} hover:${theme.text}`}
            >
              Clear history
            </button>
          )}
        </div>
        
        {recentlyPlayed.length === 0 ? (
          <div className={`text-center py-16 ${theme.textMuted}`}>
            <History size={48} className="mx-auto mb-4 opacity-30" />
            <p>No listening history yet</p>
            <p className="text-sm mt-1">Songs you play will appear here!</p>
          </div>
        ) : (
          <div className="space-y-1">
            {historySongs.map((song, index) => (
              <div key={`${song.id}-${index}`} className="flex items-center gap-3">
                <span className={`w-6 text-sm ${theme.textMuted} text-right`}>{index + 1}</span>
                <div className="flex-1">
                  <SongRow song={song} songList={historySongs} />
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Keyboard shortcuts hint */}
        <div className={`p-4 rounded-xl ${theme.bgTertiary} border ${theme.border}`}>
          <h3 className={`font-medium mb-2 ${theme.text}`}>⌨️ Keyboard Shortcuts</h3>
          <div className={`grid grid-cols-2 gap-2 text-sm ${theme.textSecondary}`}>
            <div><kbd className={`px-2 py-1 rounded ${theme.bgSecondary}`}>Space</kbd> Play/Pause</div>
            <div><kbd className={`px-2 py-1 rounded ${theme.bgSecondary}`}>←</kbd> Previous</div>
            <div><kbd className={`px-2 py-1 rounded ${theme.bgSecondary}`}>→</kbd> Next</div>
            <div><kbd className={`px-2 py-1 rounded ${theme.bgSecondary}`}>↑/↓</kbd> Volume</div>
            <div><kbd className={`px-2 py-1 rounded ${theme.bgSecondary}`}>M</kbd> Mute</div>
            <div><kbd className={`px-2 py-1 rounded ${theme.bgSecondary}`}>S</kbd> Shuffle</div>
            <div><kbd className={`px-2 py-1 rounded ${theme.bgSecondary}`}>R</kbd> Repeat</div>
          </div>
        </div>
      </div>
    );
  };

  const renderPlaylists = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-bold ${theme.text}`}>Your Playlists</h1>
        <button 
          onClick={() => setShowCreatePlaylist(true)}
          className={`flex items-center gap-2 px-4 py-2 ${darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-pink-500 hover:bg-pink-400'} text-white rounded-lg font-medium transition-colors`}
        >
          <Plus size={18} />
          Create Playlist
        </button>
      </div>
      
      {userPlaylists.length === 0 ? (
        <div className={`text-center py-16 ${theme.textMuted}`}>
          <ListMusic size={48} className="mx-auto mb-4 opacity-30" />
          <p>No playlists yet</p>
          <p className="text-sm mt-1">Create one to start organizing your favorites!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {userPlaylists.map(playlist => (
            <PlaylistCard 
              key={playlist.id} 
              playlist={playlist}
              onClick={() => { setSelectedPlaylist(playlist); setCurrentView('playlist-detail'); }}
            />
          ))}
        </div>
      )}
    </div>
  );

  const renderPlaylistDetail = () => {
    if (!selectedPlaylist) return null;
    const deviceId = getDeviceId();
    const isOwner = userPlaylists.some(p => p.id === selectedPlaylist.id);
    const canDelete = isOwner && (!selectedPlaylist.isPublic || selectedPlaylist.createdBy === deviceId);
    
    return (
      <div className="space-y-6">
        <button 
          onClick={() => { setSelectedPlaylist(null); setCurrentView(isOwner ? 'playlists' : 'explore'); }}
          className={`flex items-center gap-1 ${theme.textSecondary} hover:${theme.text}`}
        >
          <ChevronLeft size={18} />
          Back
        </button>
        
        <div className="flex items-end gap-6">
          <div className={`w-40 h-40 rounded-xl bg-gradient-to-br ${darkMode ? 'from-purple-500 to-indigo-600' : 'from-pink-500 to-rose-600'} flex items-center justify-center shadow-2xl`}>
            <ListMusic size={48} className="text-white/80" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className={`text-sm ${theme.textMuted} uppercase tracking-wider`}>Playlist</p>
              {selectedPlaylist.isPublic ? (
                <span className="flex items-center gap-1 text-xs text-green-400">
                  <Globe size={12} /> Public
                </span>
              ) : (
                <span className={`flex items-center gap-1 text-xs ${theme.textMuted}`}>
                  <Lock size={12} /> Private
                </span>
              )}
            </div>
            <h1 className={`text-3xl font-bold ${theme.text}`}>{selectedPlaylist.name}</h1>
            <p className={theme.textSecondary}>
              {selectedPlaylist.songs.length} songs
            </p>
            
            {/* Download Buttons - visible to everyone */}
            {selectedPlaylist.songs.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={() => downloadPlaylistM3U(selectedPlaylist)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${theme.bgTertiary} ${theme.text} border ${theme.border} hover:border-green-500/30 transition-colors`}
                >
                  <Download size={14} />
                  Download .m3u
                </button>
                <button
                  onClick={() => downloadPlaylistZip(selectedPlaylist)}
                  disabled={isDownloadingZip}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${theme.bgTertiary} ${theme.text} border ${theme.border} hover:border-green-500/30 transition-colors disabled:opacity-50`}
                >
                  {isDownloadingZip ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Downloading {zipProgress.current}/{zipProgress.total}...
                    </>
                  ) : (
                    <>
                      <Download size={14} />
                      Download All Songs
                    </>
                  )}
                </button>
              </div>
            )}
            
            {isOwner && (
              <div className="flex gap-2 mt-4">
                {!selectedPlaylist.isPublic ? (
                  <button
                    onClick={() => initiatePublish(selectedPlaylist.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-green-600 hover:bg-green-500' : 'bg-green-500 hover:bg-green-400'} text-white`}
                  >
                    <Globe size={14} />
                    Publish to Community
                  </button>
                ) : (
                  <button
                    onClick={() => unpublishPlaylist(selectedPlaylist.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${theme.bgTertiary} ${theme.text} border ${theme.border}`}
                  >
                    <Lock size={14} />
                    Unpublish
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => deletePlaylist(selectedPlaylist.id)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-red-500/20 text-red-400 border border-red-500/30"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        
        {selectedPlaylist.songs.length === 0 ? (
          <div className={`text-center py-12 ${theme.textMuted}`}>
            <Music size={48} className="mx-auto mb-4 opacity-30" />
            <p>This playlist is empty</p>
          </div>
        ) : (
          <div className="space-y-1">
            {(() => {
              const playlistSongs = selectedPlaylist.songs.map(songId => songs.find(s => s.id === songId)).filter(Boolean);
              return playlistSongs.map(song => (
                <SongRow 
                  key={song.id} 
                  song={song}
                  songList={playlistSongs}
                  inPlaylist={isOwner} 
                  playlistId={selectedPlaylist.id} 
                />
              ));
            })()}
          </div>
        )}
      </div>
    );
  };

  const renderFanArt = () => (
    <div className="space-y-6">
      <h1 className={`text-2xl font-bold ${theme.text}`}>Fan Art Gallery</h1>
      
      <div className={`text-center py-16 ${theme.textMuted}`}>
        <Construction size={64} className={`mx-auto mb-4 ${darkMode ? 'text-purple-400' : 'text-pink-400'}`} />
        <h2 className={`text-xl font-semibold mb-2 ${theme.text}`}>Under Construction</h2>
        <p>This feature is coming soon!</p>
        <p className="text-sm mt-2">Upload fan art to be used as song thumbnails.</p>
      </div>
    </div>
  );

  const renderSoundBites = () => (
    <div className="space-y-6">
      <h1 className={`text-2xl font-bold ${theme.text}`}>Sound Bites</h1>
      
      <div className={`text-center py-16 ${theme.textMuted}`}>
        <Construction size={64} className={`mx-auto mb-4 ${darkMode ? 'text-purple-400' : 'text-pink-400'}`} />
        <h2 className={`text-xl font-semibold mb-2 ${theme.text}`}>Under Construction</h2>
        <p>This feature is coming soon!</p>
        <p className="text-sm mt-2">Funny clips and memorable moments.</p>
      </div>
    </div>
  );

  const renderAdmin = () => (
    <div className="space-y-6">
      <h1 className={`text-2xl font-bold ${theme.text}`}>🔒 Admin Panel</h1>
      
      {!isAdmin ? (
        <div className={`p-6 rounded-xl ${theme.bgTertiary} border ${theme.border}`}>
          <p className={`${theme.textSecondary}`}>You don't have admin access.</p>
          <p className={`text-sm ${theme.textMuted} mt-2`}>
            Device ID: {getDeviceId()}
          </p>
        </div>
      ) : (
        <>
          <div className={`p-4 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400`}>
            ✓ Admin access verified
          </div>
          
          <div>
            <h2 className={`text-lg font-semibold mb-4 ${theme.text}`}>All Community Playlists ({communityPlaylists.length})</h2>
            <div className="space-y-2">
              {communityPlaylists.map(playlist => (
                <div 
                  key={playlist.id}
                  className={`flex items-center justify-between p-4 rounded-xl ${theme.bgTertiary} border ${theme.border}`}
                >
                  <div>
                    <p className={`font-medium ${theme.text}`}>{playlist.name}</p>
                    <p className={`text-sm ${theme.textMuted}`}>
                      {playlist.songs.length} songs • by {playlist.displayName || 'Anonymous'}
                    </p>
                    <p className={`text-xs ${theme.textMuted}`}>Device: {playlist.createdBy}</p>
                  </div>
                  <button
                    onClick={() => adminDeletePlaylist(playlist.id)}
                    className="px-3 py-1.5 rounded-lg text-sm bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div 
      className={`min-h-screen ${theme.bg} ${theme.text} transition-colors`} 
      style={{
        fontFamily: "'Outfit', sans-serif",
        backgroundColor: darkMode ? '#09090b' : '#fdf2f4'
      }}
    >
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Righteous&display=swap" rel="stylesheet" />
      
      

      <div className="relative z-10 flex h-screen">
        {/* Mobile Header */}
        <div className={`md:hidden fixed top-0 left-0 right-0 z-40 border-b ${theme.border}`} style={{ backgroundColor: darkMode ? '#18181b' : '#ffffff' }}>
          <div className="flex items-center justify-between px-4 py-3">
            <button onClick={() => setMobileMenuOpen(true)} className={theme.text}>
              <Menu size={24} />
            </button>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${darkMode ? 'from-purple-500 to-purple-700' : 'from-pink-400 to-pink-500'} flex items-center justify-center`}>
                <span className="text-sm">{darkMode ? '🦇' : '🐰'}</span>
              </div>
              <span className={`font-bold bg-gradient-to-r ${darkMode ? 'from-purple-300 to-purple-400' : 'from-pink-400 to-pink-500'} bg-clip-text text-transparent`} style={{fontFamily: "'Righteous', cursive"}}>
                {darkMode ? 'Shippa' : 'Pippa'}
              </span>
            </div>
            <button onClick={() => setDarkMode(!darkMode)} className={theme.textMuted}>
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black/60 z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar - Hidden on mobile, slide-out when menu open */}
        <aside 
          className={`
            fixed md:relative inset-y-0 left-0 z-50
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
            ${sidebarCollapsed ? 'w-16' : 'w-64'} 
            flex-shrink-0 ${theme.bgSecondary} border-r ${theme.border} flex flex-col transition-all duration-300
          `}
          style={{ backgroundColor: darkMode ? '#18181b' : '#ffffff' }}
        >
          {/* Logo */}
          <div className={`p-4 flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${darkMode ? 'from-purple-500 to-purple-700' : 'from-pink-400 to-pink-500'} flex items-center justify-center flex-shrink-0`}>
                <span className="text-xl">{darkMode ? '🦇' : '🐰'}</span>
              </div>
              {!sidebarCollapsed && (
                <span className={`font-bold text-lg bg-gradient-to-r ${darkMode ? 'from-purple-300 to-purple-400' : 'from-pink-400 to-pink-500'} bg-clip-text text-transparent`} style={{fontFamily: "'Righteous', cursive"}}>
                  {darkMode ? 'Shippa Karaoke' : 'Pippa Karaoke'}
                </span>
              )}
            </div>
            {/* Mobile close button */}
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className={`md:hidden p-1 ${theme.textMuted}`}
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {[
              { id: 'home', icon: Home, label: 'Home' },
              { id: 'search', icon: Search, label: 'Search' },
              { id: 'random', icon: Shuffle, label: 'Random' },
              { id: 'explore', icon: Compass, label: 'Explore' },
              { id: 'artists', icon: Mic2, label: 'Artists' },
              { id: 'about', icon: Info, label: 'About' },
              { id: 'credits', icon: Award, label: 'Credits' },
              { id: 'bugreport', icon: Bug, label: 'Bug Report' },
            ].map(item => (
              <button
                key={item.id}
                onClick={() => { item.action ? item.action() : setCurrentView(item.id); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  currentView === item.id 
                    ? `${theme.bgActive} ${darkMode ? 'text-purple-400 border-purple-500/30' : 'text-pink-500 border-pink-500/30'} border` 
                    : `${theme.textSecondary} ${theme.bgHover}`
                } ${sidebarCollapsed ? 'justify-center' : ''}`}
                title={sidebarCollapsed ? item.label : ''}
              >
                <item.icon size={20} />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
            ))}
            
            {/* Library Section */}
            {!sidebarCollapsed && (
              <div className={`pt-4 mt-4 border-t ${theme.border}`}>
                <p className={`px-3 text-xs font-semibold uppercase tracking-wider ${theme.textMuted} mb-2`}>Library</p>
              </div>
            )}
            {[
              { id: 'favorites', icon: Heart, label: 'Favorites' },
              { id: 'playlists', icon: ListMusic, label: 'My Playlists' },
              { id: 'history', icon: History, label: 'Recently Played' },
            ].map(item => (
              <button
                key={item.id}
                onClick={() => { setCurrentView(item.id); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  currentView === item.id 
                    ? `${theme.bgActive} ${darkMode ? 'text-purple-400 border-purple-500/30' : 'text-pink-500 border-pink-500/30'} border` 
                    : `${theme.textSecondary} ${theme.bgHover}`
                } ${sidebarCollapsed ? 'justify-center' : ''}`}
                title={sidebarCollapsed ? item.label : ''}
              >
                <item.icon size={20} />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
            ))}
            
            {/* Community Section */}
            {!sidebarCollapsed && (
              <div className={`pt-4 mt-4 border-t ${theme.border}`}>
                <p className={`px-3 text-xs font-semibold uppercase tracking-wider ${theme.textMuted} mb-2`}>Community</p>
              </div>
            )}
            {[
              { id: 'fanart', icon: Image, label: 'Fan Art' },
              { id: 'soundbites', icon: Volume2, label: 'Sound Bites' },
            ].map(item => (
              <button
                key={item.id}
                onClick={() => { setCurrentView(item.id); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  currentView === item.id 
                    ? `${theme.bgActive} ${darkMode ? 'text-purple-400 border-purple-500/30' : 'text-pink-500 border-pink-500/30'} border` 
                    : `${theme.textSecondary} ${theme.bgHover}`
                } ${sidebarCollapsed ? 'justify-center' : ''}`}
                title={sidebarCollapsed ? item.label : ''}
              >
                <item.icon size={20} />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
            ))}
          </nav>
          
          {/* Theme Toggle */}
          <div className={`p-4 border-t ${theme.border}`}>
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} gap-2`}>
              {!sidebarCollapsed && (
                <>
                  {/* Pippa (Light mode) image */}
                  <div className={`w-8 h-8 rounded-full overflow-hidden border-2 transition-all ${!darkMode ? 'border-pink-400 scale-110' : 'border-transparent opacity-50'}`}>
                    {/* Replace src with actual Pippa image */}
                    <div className="w-full h-full bg-gradient-to-br from-pink-300 to-pink-400 flex items-center justify-center text-xs">
                      🐰
                    </div>
                  </div>
                </>
              )}
              
              {/* Toggle Switch */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`relative w-14 h-7 rounded-full transition-colors ${darkMode ? 'bg-purple-900' : 'bg-pink-300'}`}
              >
                <div 
                  className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300 ${darkMode ? 'left-8' : 'left-1'}`}
                />
              </button>
              
              {!sidebarCollapsed && (
                <>
                  {/* Shippa (Dark mode) image */}
                  <div className={`w-8 h-8 rounded-full overflow-hidden border-2 transition-all ${darkMode ? 'border-purple-400 scale-110' : 'border-transparent opacity-50'}`}>
                    {/* Replace src with actual Shippa image */}
                    <div className="w-full h-full bg-gradient-to-br from-purple-600 to-purple-900 flex items-center justify-center text-xs">
                      🦇
                    </div>
                  </div>
                </>
              )}
            </div>
            {!sidebarCollapsed && (
              <p className={`text-xs text-center mt-2 ${theme.textMuted}`}>
                {darkMode ? 'Shippa Mode' : 'Pippa Mode'}
              </p>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main 
          className="flex-1 flex flex-col overflow-hidden pt-14 md:pt-0"
          style={{ backgroundColor: darkMode ? '#09090b' : '#fdf2f4' }}
        >
          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {currentView === 'home' && renderHome()}
            {currentView === 'search' && renderSearch()}
            {currentView === 'random' && renderRandom()}
            {currentView === 'explore' && renderExplore()}
            {currentView === 'artists' && renderArtists()}
            {currentView === 'about' && renderAbout()}
            {currentView === 'bugreport' && renderBugReport()}
            {currentView === 'credits' && renderCredits()}
            {currentView === 'favorites' && renderFavorites()}
            {currentView === 'history' && renderHistory()}
            {currentView === 'playlists' && renderPlaylists()}
            {currentView === 'playlist-detail' && renderPlaylistDetail()}
            {currentView === 'fanart' && renderFanArt()}
            {currentView === 'soundbites' && renderSoundBites()}
            {currentView === 'admin' && renderAdmin()}
          </div>
          
          {/* Player Bar */}
          <div 
            className={`border-t ${theme.border} ${theme.bgSecondary}`}
            style={{ backgroundColor: darkMode ? '#18181b' : '#ffffff' }}
          >
            <div className="px-4 py-3">
              <div className="flex items-center gap-4">
                {/* Current Song */}
                <div className="flex items-center gap-3 w-64 flex-shrink-0">
                  {currentSong ? (
                    <>
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${darkMode ? 'from-purple-400/50 to-indigo-500/50' : 'from-pink-400/50 to-rose-500/50'} flex items-center justify-center flex-shrink-0`}>
                        <Music size={20} className={theme.textMuted} />
                      </div>
                      <div className="min-w-0">
                        <p className={`font-medium truncate text-sm ${theme.text}`}>{currentSong.title}</p>
                        <p className={`text-xs truncate ${theme.textMuted}`}>{currentSong.artist}</p>
                      </div>
                      <button 
                        onClick={() => toggleFavorite(currentSong.id)}
                        className={favorites.includes(currentSong.id) ? 'text-pink-500' : theme.textMuted}
                      >
                        <Heart size={16} fill={favorites.includes(currentSong.id) ? 'currentColor' : 'none'} />
                      </button>
                    </>
                  ) : (
                    <p className={theme.textMuted}>No song playing</p>
                  )}
                </div>
                
                {/* Controls */}
                <div className="flex-1 flex flex-col items-center gap-1">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => setShuffle(!shuffle)}
                      className={`p-1.5 rounded-full transition-colors ${shuffle ? (darkMode ? 'text-purple-400' : 'text-pink-400') : `${theme.textMuted} hover:text-white`}`}
                    >
                      <Shuffle size={16} />
                    </button>
                    <button onClick={handlePrev} className={`p-1.5 ${theme.textMuted} hover:text-white transition-colors`}>
                      <SkipBack size={18} />
                    </button>
                    <button 
                      onClick={() => currentSong && setIsPlaying(!isPlaying)}
                      className={`p-2.5 ${darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-pink-500 hover:bg-pink-400'} rounded-full transition-colors`}
                    >
                      {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
                    </button>
                    <button onClick={handleNext} className={`p-1.5 ${theme.textMuted} hover:text-white transition-colors`}>
                      <SkipForward size={18} />
                    </button>
                    <button 
                      onClick={() => setRepeat(!repeat)}
                      className={`p-1.5 rounded-full transition-colors ${repeat ? (darkMode ? 'text-purple-400' : 'text-pink-400') : `${theme.textMuted} hover:text-white`}`}
                    >
                      <Repeat size={16} />
                    </button>
                  </div>
                  
                  {/* Progress */}
                  <div className="w-full max-w-lg flex items-center gap-2">
                    <span className={`text-xs ${theme.textMuted} w-10 text-right`}>
                      {currentSong ? formatTime((progress / 100) * currentSong.durationSec) : '0:00'}
                    </span>
                    <div 
                      className={`flex-1 h-1 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'} rounded-full overflow-hidden cursor-pointer`}
                      onClick={handleSeek}
                    >
                      <div 
                        className={`h-full ${darkMode ? 'bg-purple-500' : 'bg-pink-500'} transition-all`}
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <span className={`text-xs ${theme.textMuted} w-10`}>
                      {currentSong?.duration || '0:00'}
                    </span>
                  </div>
                </div>
                
                {/* Volume & Queue */}
                <div className="flex items-center gap-3 w-40 justify-end">
                  <button 
                    onClick={() => setIsMuted(!isMuted)}
                    className={`${theme.textMuted} hover:text-white transition-colors`}
                  >
                    {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={(e) => { setVolume(parseFloat(e.target.value)); setIsMuted(false); }}
                    className={`w-20 ${darkMode ? 'accent-purple-500' : 'accent-pink-500'}`}
                  />
                  <button 
                    onClick={() => setShowQueue(!showQueue)}
                    className={`p-1.5 rounded transition-colors ${showQueue ? (darkMode ? 'text-purple-400' : 'text-pink-400') : `${theme.textMuted} hover:text-white`}`}
                  >
                    <List size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Queue Panel */}
        {showQueue && (
          <aside 
            className={`w-80 flex-shrink-0 ${theme.bgSecondary} border-l ${theme.border} flex flex-col`}
            style={{ backgroundColor: darkMode ? '#18181b' : '#ffffff' }}
          >
            <div className={`p-4 border-b ${theme.border} flex items-center justify-between`}>
              <h3 className={`font-semibold ${theme.text}`}>Queue</h3>
              <button onClick={() => setShowQueue(false)} className={theme.textMuted}>
                <X size={18} />
              </button>
            </div>
            
            {currentSong && (
              <div className={`p-4 border-b ${theme.border}`}>
                <p className={`text-xs ${theme.textMuted} uppercase tracking-wider mb-2`}>Now Playing</p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded bg-gradient-to-br ${darkMode ? 'from-purple-400/50 to-indigo-500/50' : 'from-pink-400/50 to-rose-500/50'} flex items-center justify-center`}>
                    <Music size={16} className={theme.textMuted} />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${theme.text}`}>{currentSong.title}</p>
                    <p className={`text-xs truncate ${theme.textMuted}`}>{currentSong.artist}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex-1 overflow-y-auto p-4">
              <p className={`text-xs ${theme.textMuted} uppercase tracking-wider mb-2`}>Up Next ({queue.length})</p>
              {queue.length === 0 ? (
                <p className={`text-sm ${theme.textMuted} text-center py-8`}>Queue is empty</p>
              ) : (
                <div className="space-y-2">
                  {queue.map((song, i) => (
                    <div key={song.id} className={`flex items-center gap-2 p-2 rounded-lg ${theme.bgHover} group`}>
                      <span className={`text-xs ${theme.textMuted} w-4`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${theme.text}`}>{song.title}</p>
                        <p className={`text-xs truncate ${theme.textMuted}`}>{song.artist}</p>
                      </div>
                      <button 
                        onClick={() => removeFromQueue(song.id)}
                        className="opacity-0 group-hover:opacity-100 text-red-400"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        )}
      </div>

      {/* ============================================ */}
      {/* MODALS */}
      {/* ============================================ */}
      
      {/* Add to Playlist Modal */}
      {showAddToPlaylist && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowAddToPlaylist(false)}>
          <div 
            className={`${theme.bgTertiary} border ${theme.border} rounded-2xl p-6 w-full max-w-md m-4`} 
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: darkMode ? '#27272a' : '#fff1f2' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-semibold ${theme.text}`}>Add to Playlist</h3>
              <button onClick={() => setShowAddToPlaylist(false)} className={theme.textMuted}>
                <X size={20} />
              </button>
            </div>
            
            {selectedSongForPlaylist && (
              <p className={`${theme.textSecondary} mb-4`}>
                Adding: <span className={theme.text}>{selectedSongForPlaylist.title}</span>
              </p>
            )}
            
            {userPlaylists.length === 0 ? (
              <p className={`${theme.textMuted} text-center py-4`}>No playlists yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {userPlaylists.map(playlist => (
                  <button
                    key={playlist.id}
                    onClick={() => addSongToPlaylist(playlist.id)}
                    className={`w-full p-3 rounded-lg ${theme.input} border hover:border-pink-500/30 text-left transition-all flex items-center justify-between ${theme.text}`}
                  >
                    <span>{playlist.name}</span>
                    <span className={`text-sm ${theme.textMuted}`}>{playlist.songs.length} songs</span>
                  </button>
                ))}
              </div>
            )}
            
            <button
              onClick={() => { setShowAddToPlaylist(false); setShowCreatePlaylist(true); }}
              className={`w-full mt-4 p-3 rounded-lg border border-dashed ${theme.border} hover:border-pink-500/50 ${theme.textSecondary} hover:text-pink-400 transition-all flex items-center justify-center gap-2`}
            >
              <Plus size={18} />
              Create New Playlist
            </button>
          </div>
        </div>
      )}

      {/* Create Playlist Modal */}
      {showCreatePlaylist && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCreatePlaylist(false)}>
          <div 
            className={`${theme.bgTertiary} border ${theme.border} rounded-2xl p-6 w-full max-w-md m-4`} 
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: darkMode ? '#27272a' : '#fff1f2' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-semibold ${theme.text}`}>Create Playlist</h3>
              <button onClick={() => setShowCreatePlaylist(false)} className={theme.textMuted}>
                <X size={20} />
              </button>
            </div>
            
            <input
              type="text"
              value={newPlaylistName}
              onChange={(e) => setNewPlaylistName(e.target.value)}
              placeholder="Playlist name..."
              className={`w-full px-4 py-3 ${theme.input} border rounded-xl focus:outline-none focus:border-pink-500/50 mb-4 ${theme.text}`}
              autoFocus
            />
            
            <p className={`text-sm ${theme.textMuted} mb-4`}>
              💡 Tip: Add songs first, then publish to share with the community
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowCreatePlaylist(false)}
                className={`flex-1 py-3 rounded-xl border ${theme.border} ${theme.textSecondary} hover:${theme.text} ${theme.bgHover} transition-all`}
              >
                Cancel
              </button>
              <button
                onClick={createPlaylist}
                className={`flex-1 py-3 rounded-xl ${darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-pink-500 hover:bg-pink-400'} text-white font-medium transition-colors`}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Nickname Prompt Modal */}
      {showNicknamePrompt && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => { setShowNicknamePrompt(false); setPendingPublishId(null); }}>
          <div 
            className={`${theme.bgTertiary} border ${theme.border} rounded-2xl p-6 w-full max-w-md m-4`} 
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: darkMode ? '#27272a' : '#fff1f2' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-semibold ${theme.text}`}>Set Your Nickname</h3>
              <button onClick={() => { setShowNicknamePrompt(false); setPendingPublishId(null); }} className={theme.textMuted}>
                <X size={20} />
              </button>
            </div>
            
            <p className={`text-sm ${theme.textMuted} mb-4`}>
              This name will be shown on your public playlists
            </p>
            
            <input
              type="text"
              value={nicknameInput}
              onChange={(e) => setNicknameInput(e.target.value)}
              placeholder="Enter a nickname..."
              className={`w-full px-4 py-3 ${theme.input} border rounded-xl focus:outline-none focus:border-pink-500/50 mb-4 ${theme.text}`}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && saveNickname()}
            />
            
            <div className="flex gap-3">
              <button
                onClick={() => { setShowNicknamePrompt(false); setPendingPublishId(null); }}
                className={`flex-1 py-3 rounded-xl border ${theme.border} ${theme.textSecondary} ${theme.bgHover} transition-all`}
              >
                Cancel
              </button>
              <button
                onClick={saveNickname}
                className={`flex-1 py-3 rounded-xl ${darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-pink-500 hover:bg-pink-400'} text-white font-medium transition-colors`}
              >
                Save & Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Fan Art Modal */}
      {showUploadArt && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowUploadArt(false)}>
          <div 
            className={`${theme.bgTertiary} border ${theme.border} rounded-2xl p-6 w-full max-w-md m-4`} 
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: darkMode ? '#27272a' : '#fff1f2' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-semibold ${theme.text}`}>Upload Fan Art</h3>
              <button onClick={() => setShowUploadArt(false)} className={theme.textMuted}>
                <X size={20} />
              </button>
            </div>
            
            <div className={`border-2 border-dashed ${theme.border} rounded-xl p-8 text-center mb-4`}>
              <Upload size={32} className={`mx-auto mb-2 ${theme.textMuted}`} />
              <p className={theme.textSecondary}>Click to upload or drag and drop</p>
              <p className={`text-sm ${theme.textMuted}`}>PNG, JPG, GIF up to 5MB</p>
            </div>
            
            <input
              type="text"
              value={newArtTags}
              onChange={(e) => setNewArtTags(e.target.value)}
              placeholder="Tags (comma separated): metal, rock, anime..."
              className={`w-full px-4 py-3 ${theme.input} border rounded-xl focus:outline-none focus:border-pink-500/50 mb-3 ${theme.text}`}
            />
            
            <input
              type="text"
              value={newArtCredit}
              onChange={(e) => setNewArtCredit(e.target.value)}
              placeholder="Your name (optional)"
              className={`w-full px-4 py-3 ${theme.input} border rounded-xl focus:outline-none focus:border-pink-500/50 mb-4 ${theme.text}`}
            />
            
            <button
              onClick={uploadFanArt}
              className={`w-full py-3 rounded-xl ${darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-pink-500 hover:bg-pink-400'} text-white font-medium transition-colors`}
            >
              Upload
            </button>
          </div>
        </div>
      )}

      {/* Upload Sound Bite Modal */}
      {showUploadSoundBite && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowUploadSoundBite(false)}>
          <div 
            className={`${theme.bgTertiary} border ${theme.border} rounded-2xl p-6 w-full max-w-md m-4`} 
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: darkMode ? '#27272a' : '#fff1f2' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-semibold ${theme.text}`}>Upload Sound Bite</h3>
              <button onClick={() => setShowUploadSoundBite(false)} className={theme.textMuted}>
                <X size={20} />
              </button>
            </div>
            
            <div className={`border-2 border-dashed ${theme.border} rounded-xl p-8 text-center mb-4`}>
              <Volume2 size={32} className={`mx-auto mb-2 ${theme.textMuted}`} />
              <p className={theme.textSecondary}>Click to upload audio file</p>
              <p className={`text-sm ${theme.textMuted}`}>MP3, WAV up to 2MB</p>
            </div>
            
            <input
              type="text"
              value={newSoundBiteName}
              onChange={(e) => setNewSoundBiteName(e.target.value)}
              placeholder="Sound bite name..."
              className={`w-full px-4 py-3 ${theme.input} border rounded-xl focus:outline-none focus:border-pink-500/50 mb-3 ${theme.text}`}
            />
            
            <input
              type="text"
              value={newSoundBiteCredit}
              onChange={(e) => setNewSoundBiteCredit(e.target.value)}
              placeholder="Your name (optional)"
              className={`w-full px-4 py-3 ${theme.input} border rounded-xl focus:outline-none focus:border-pink-500/50 mb-4 ${theme.text}`}
            />
            
            <button
              onClick={uploadSoundBite}
              className={`w-full py-3 rounded-xl ${darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-pink-500 hover:bg-pink-400'} text-white font-medium transition-colors`}
            >
              Upload
            </button>
          </div>
        </div>
      )}
    </div>
  );
}